import { fetcher } from '@/lib/api/fetch'
import { objectIdIsEmpty } from '@/lib/utils/common'

/**
 * This represents some generic auth provider API, like Firebase.
 */
const authProvider = {
  isAuthenticated: false,
  setuser(user, callback) {
    authProvider.isAuthenticated = !!user
    callback()
  },
  signin(callback) {
    authProvider.isAuthenticated = true
    callback()
  },
  signout(callback) {
    authProvider.isAuthenticated = false
    callback()
  },
  refresh(callback) {
    callback()
  },
}

const getLocalStorageAuth = () => {
  const auth = localStorage.getItem('fac_auth')
  if (auth) {
    return JSON.parse(auth)
  }
  return ''
}

const setLocalStorageAuth = user => {
  localStorage.setItem('fac_auth', JSON.stringify(user))
}

const removeLocalStorageAuth = () => {
  localStorage.removeItem('fac_auth')
}

const getRBACProfile = async context => {
  if (
    !context.user.organization ||
    objectIdIsEmpty(context.user.organization.id)
  )
    return null

  const res = await fetcher(
    '/api/admin/user/profile/' + context.user.organization.id,
    { method: 'GET' },
    context,
  )

  // This is only called when another call has been made
  // meaning we should expect that the user is authenticated
  // There is a very small chance that the last call was
  // made milliseconds before the user is logged out, but
  // unless it becomes an issue we can assume we are good here

  return await res.json()
}

// This one needs to stay a function() instead of an arrow function
// so that we can access this. It's added to the user object as a
// helper to check if the user is authorized to perform an action
function isAuthorized(permission) {
  if (!this.RBACProfile?.permissions) {
    return false
  }

  const parts = permission.split('.')
  try {
    if (parts.length < 2) {
      const [pName] = parts
      // This is a top level permission
      const p = this.RBACProfile.permissions[pName]

      if (typeof p === 'object') {
        // This is asking for permissions for a full category
        const keys = Object.keys(p)
        // If any key is true we return true
        for (const key of keys) {
          if (p[key]) return true
        }
      }

      // This is asking for a generic top level permission
      return !!p
    }
  } catch {
    console.error('An error occurred during RBAC top level check', permission)
    return false
  }

  // This is a permission that is a sub permission of a category
  const [category, pName] = parts
  try {
    return !!this.RBACProfile.permissions[category][pName]
  } catch {
    console.error('An error occurred during RBAC check', category, pName)
    return false
  }
}

const recipientHasAccess = (user, recipients) => {
  // Give the owner full permissions
  if (user.id === user.organization.owner) return true
  if (!recipients || recipients.length < 1) return false

  for (let r of recipients) {
    if (!objectIdIsEmpty(r.userId) && user.id === r.userId) return true
    if (r.userGroupId) {
      const member = user.organization.members.find(m => m.memberId === user.id)
      if (member.userGroup && member.userGroup === r.userGroupId) return true
    }
    if (!objectIdIsEmpty(r)) {
      // This can occur when using the group only chooser
      const member = user.organization.members.find(m => m.memberId === user.id)
      if (member.userGroup && member.userGroup === r) return true
    }
  }
  return false
}

export {
  authProvider,
  getLocalStorageAuth,
  setLocalStorageAuth,
  removeLocalStorageAuth,
  getRBACProfile,
  isAuthorized,
  recipientHasAccess,
}
